import { useId, useRef, useState } from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { BookOpen, CornerDownLeft, RefreshCw, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/FormControls';
import { screenGuidance } from '@/lib/fieldGuidance';

interface GuideField {
  label: string;
  help: string;
  required: boolean;
  disabled: boolean;
  element: HTMLElement;
}

function visibleFields(scope: ParentNode): GuideField[] {
  return Array.from(scope.querySelectorAll<HTMLElement>('[data-field-help]'))
    .filter(
      (element) =>
        !element.closest('[data-user-guide], [inert]') && element.getClientRects().length > 0,
    )
    .map((element) => ({
      label: element.dataset.fieldLabel || 'Field',
      help: element.dataset.fieldHelp || '',
      required:
        element.hasAttribute('required') ||
        element.getAttribute('aria-required') === 'true' ||
        (element.dataset.fieldLabel || '').includes('*'),
      disabled: element.matches(':disabled, [aria-disabled="true"]'),
      element,
    }));
}

/** Reads only authored help metadata, never entered values or option text. */
function UserGuideContent({ compact = false, pathname }: { compact?: boolean; pathname: string }) {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [fields, setFields] = useState<GuideField[]>([]);
  const focusTarget = useRef<HTMLElement | null>(null);
  const sourceScope = useRef<ParentNode>(document);
  const guide = screenGuidance(pathname);
  const filtered = fields.filter((field) =>
    `${field.label} ${field.help}`.toLowerCase().includes(search.toLowerCase().trim()),
  );
  const refresh = () => setFields(visibleFields(sourceScope.current));
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (next) {
          sourceScope.current =
            document.activeElement?.closest('[role="dialog"], [data-field-guide-scope]') ??
            document;
          refresh();
          setSearch('');
        } else setFields([]);
        setOpen(next);
      }}
    >
      <Dialog.Trigger asChild>
        <button
          type="button"
          aria-label="Open user guide for this screen"
          title="User guide"
          className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-xl border border-border bg-background/40 px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <BookOpen className="h-4 w-4" aria-hidden="true" />
          {!compact && <span>User guide</span>}
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          data-user-guide
          className="fixed inset-x-3 top-1/2 z-[111] flex max-h-[90dvh] -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl outline-none sm:left-1/2 sm:right-auto sm:w-[640px] sm:max-w-[calc(100vw-24px)] sm:-translate-x-1/2"
          onCloseAutoFocus={(event) => {
            const target = focusTarget.current;
            focusTarget.current = null;
            if (target?.isConnected) {
              event.preventDefault();
              requestAnimationFrame(() => {
                target.scrollIntoView({ block: 'center' });
                target.focus({ preventScroll: true });
              });
            }
          }}
        >
          <header className="border-b border-border p-4 pr-16 sm:p-6 sm:pr-16">
            <Dialog.Title className="text-lg font-semibold">User guide</Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-muted-foreground">
              {guide.title}. Read field usage and return to the field you need.
            </Dialog.Description>
            <Dialog.Close
              aria-label="Close user guide"
              className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-xl hover:bg-accent"
            >
              <X className="h-5 w-5" />
            </Dialog.Close>
          </header>
          <div className="min-h-0 space-y-5 overflow-y-auto overscroll-contain p-4 sm:p-6">
            <section
              aria-labelledby={`${uid}-steps`}
              className="rounded-xl border border-primary/20 bg-primary/5 p-4"
            >
              <h2 id={`${uid}-steps`} className="text-sm font-semibold">
                How to use this screen
              </h2>
              <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
                {guide.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </section>
            <section className="space-y-3" aria-labelledby={`${uid}-fields`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 id={`${uid}-fields`} className="font-semibold">
                  Fields on this screen ({fields.length})
                </h2>
                <button
                  type="button"
                  onClick={refresh}
                  className="inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-xs text-primary hover:bg-accent"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Refresh fields
                </button>
              </div>
              <label className="relative block">
                <span className="sr-only">Search field help</span>
                <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Find a field or topic…"
                  className="pl-10"
                />
              </label>
              <p className="text-xs leading-relaxed text-muted-foreground">
                This list describes the currently visible fields. Open a section first to include
                its fields. Disabled fields can be read here but cannot be changed.
              </p>
              {!fields.length && (
                <p
                  role="status"
                  className="rounded-xl border border-border p-4 text-sm text-muted-foreground"
                >
                  No entry fields are visible. Use the steps above, or open an add/edit form and
                  reopen this guide.
                </p>
              )}
              {!!fields.length && !filtered.length && (
                <p role="status" className="py-4 text-sm text-muted-foreground">
                  No matching field help. Try another word.
                </p>
              )}
              {filtered.map((field, index) => (
                <article
                  key={index}
                  className="rounded-xl border border-border bg-background/40 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-sm font-semibold">
                      {field.label}
                      {field.required && !field.label.includes('*') ? ' *' : ''}
                    </h3>
                    <button
                      type="button"
                      disabled={field.disabled}
                      aria-label={`Go to ${field.label}`}
                      onClick={() => {
                        focusTarget.current = field.element;
                        setOpen(false);
                        setFields([]);
                      }}
                      className="inline-flex min-h-9 shrink-0 items-center gap-1 rounded-lg px-2 text-xs text-primary hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <CornerDownLeft className="h-3.5 w-3.5" />
                      Go to field
                    </button>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{field.help}</p>
                  {field.disabled && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Currently unavailable or read-only for this action.
                    </p>
                  )}
                </article>
              ))}
            </section>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function RoutedUserGuide(props: { compact?: boolean }) {
  const { pathname } = useLocation();
  return <UserGuideContent {...props} pathname={pathname} />;
}

export default function UserGuide(props: { compact?: boolean }) {
  const inRouter = useInRouterContext();
  return inRouter ? (
    <RoutedUserGuide {...props} />
  ) : (
    <UserGuideContent
      {...props}
      pathname={typeof window === 'undefined' ? '/' : window.location.pathname}
    />
  );
}
